// The ANONYMOUS persistence adapter — the public funnel's implementation of
// the seam in prequalDraftAdapter.ts.
//
// Everything token-shaped lives in here and nowhere else: the funnel pages
// never see a token, a draft id or a row version.
//
// PURE CONSTRUCTION — this hook fires no effects and makes no request of its
// own. That matters twice over. A funnel page calls it unconditionally (hooks
// cannot be conditional) even when the assisted surface has injected its own
// adapter, so an inert hook is the difference between "unused" and "quietly
// minting a second, organic draft alongside the assisted one". And it keeps
// the create strictly behind a user action: the page owns the mount effect,
// and that effect only ever READS.

import { useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { PortalApiError } from "../../api/client";
import { prequalDraftsApi, type PrequalDraftToken, type PrequalRowVersion } from "../../api/prequalDrafts";
import {
  prequalificationApi,
  type PrequalFinalizeRequest,
  type PrequalFinalizeResult,
  type PrequalRole,
} from "../../api/prequalification";
import {
  isIdentityComplete,
  type PrequalAnswers,
  type PrequalDraftAdapter,
  type PrequalDraftIdentity,
  type PrequalDraftLoad,
  type PrequalDraftSnapshot,
  type PrequalDraftWriteResult,
} from "./prequalDraftAdapter";
import { usePrequalPartnerSlug } from "./usePartnerBrand";
import { fingerprintScope } from "./prequalStorage";
import { loadAttribution } from "./prequalAttribution";

/**
 * Where the organic resume token is remembered for the rest of the tab's life.
 *
 * Without it a refresh mints a SECOND draft: the funnel's answers come back
 * from sessionStorage, the adapter has forgotten its token, and the next
 * Continue looks exactly like a first save. The visitor then accumulates
 * duplicate rows on somebody's follow-up list, each with a different link, and
 * only one of them is the one they were shown.
 *
 * Same tab, same origin, alongside the answers it belongs to — this stores no
 * secret the blob beside it does not already contain.
 *
 * SCOPED, for the same reason the answers beside it are: one browser, more
 * than one applicant. A token minted on one partner site or under one invite
 * is a live write handle on THAT person's draft, so carrying it into another
 * context would append the next client's answers to the previous client's row.
 * The stored value therefore carries the fingerprint of the context that
 * minted it; a mismatch removes it rather than merely ignoring it, and the
 * refresh-does-not-duplicate property holds unchanged within one context.
 *
 * The slot used to hold the bare token string. That is not a shape this can
 * be confused by — a base64url token is never a JSON object — so the reader
 * below rejects and removes it without needing a second, versioned key.
 */
const DRAFT_TOKEN_KEY_PREFIX = "phx:prequal:draft:";

function tokenStorageKey(role: PrequalRole): string {
  return `${DRAFT_TOKEN_KEY_PREFIX}${role.toLowerCase()}:v1`;
}

function removeKey(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function readStoredToken(role: PrequalRole, scope: string): string | null {
  const key = tokenStorageKey(role);
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    const record =
      typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    if (record === null || typeof record.token !== "string" || record.scope !== scope) {
      removeKey(key);
      return null;
    }
    return record.token;
  } catch {
    removeKey(key);
    return null;
  }
}

function writeStoredToken(role: PrequalRole, scope: string, token: string): void {
  try {
    window.sessionStorage.setItem(tokenStorageKey(role), JSON.stringify({ scope, token }));
  } catch {
    // Quota / privacy mode — the in-memory token still serves this page view.
  }
}

/**
 * Recover the bare token from the server's resume URL.
 *
 * The URL is used VERBATIM everywhere it is shown to a person. This parse
 * exists only because subsequent writes address the draft by `?draft={token}`
 * and the create response carries no bare-token field. Parsing back out is
 * strictly safer than composing the URL ourselves, which is the mistake that
 * only shows up when a real visitor's link fails.
 *
 * A URL we cannot parse yields null, and the adapter then simply stops saving
 * — the funnel keeps working out of sessionStorage.
 */
function tokenFromResumeUrl(resumeUrl: string | null | undefined): string | null {
  if (!resumeUrl) return null;
  try {
    return new URL(resumeUrl, window.location.origin).searchParams.get("draft");
  } catch {
    return null;
  }
}

function parseAnswers(answersJson: string): PrequalAnswers | null {
  try {
    const parsed: unknown = JSON.parse(answersJson);
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
      ? (parsed as PrequalAnswers)
      : null;
  } catch {
    return null;
  }
}

const FRIENDLY_SAVE_ERROR = "We couldn't save your progress just now.";
const CONFLICT_MESSAGE = "This application was updated somewhere else. Reload it and try again.";

/** Never surface raw HTTP text to a visitor; keep a genuine server message. */
function friendly(error: unknown): string {
  const raw = error instanceof Error ? error.message.trim() : "";
  return !raw || /HTTP \d+|Request failed/i.test(raw) ? FRIENDLY_SAVE_ERROR : raw;
}

interface AdapterState {
  /** The organic resume token. Null on the invited path, which resumes through
   *  the invite instead — a draft token there would be a second, permanent way
   *  back in that honours neither the invite's expiry nor its revocation. */
  draftToken: string | null;
  rowVersion: PrequalRowVersion | null;
  /** Whether a server draft is known to back this session. Seeded false: the
   *  first write is what mints it, and a read is what discovers an existing
   *  one. */
  exists: boolean;
  /**
   * The identity an ORGANIC draft holds — set at create and at resume, and
   * never by a step write (a step carries no identity, so the server's copy
   * cannot have moved). Null on the invited path, and null is what makes the
   * comparison below a no-op there.
   *
   * Finalize-by-draft takes its name and email FROM THE DRAFT, deliberately:
   * an invitee must not be able to rename themselves into somebody else's
   * follow-up list. That is right for an invite, whose sender chose the
   * address. It is wrong for an organic visitor, whose typed identity is the
   * only source there ever was — silently discarding their correction on the
   * finalize step, which is exactly when a person re-reads their own email
   * address, would send their documents somewhere they just fixed.
   */
  identityAtSave: PrequalDraftIdentity | null;
}

function normalizedIdentity(identity: PrequalDraftIdentity): PrequalDraftIdentity {
  return {
    firstName: identity.firstName.trim(),
    lastName: identity.lastName.trim(),
    email: identity.email.trim().toLowerCase(),
  };
}

/** True when the visitor changed who this application is for after it was
 *  saved. Blank fields never count as a change — that is an unfinished form,
 *  not a correction. */
function identityMoved(
  saved: PrequalDraftIdentity | null,
  submitted: PrequalDraftIdentity,
): boolean {
  if (saved === null) return false;
  const now = normalizedIdentity(submitted);
  if (!isIdentityComplete(now)) return false;
  const was = normalizedIdentity(saved);
  return (
    was.firstName !== now.firstName || was.lastName !== now.lastName || was.email !== now.email
  );
}

export function useAnonymousPrequalDraftAdapter(role: PrequalRole): PrequalDraftAdapter {
  const [params] = useSearchParams();
  const slug = usePrequalPartnerSlug();

  // Read fresh every render, consumed at call time — a token that changed in
  // the address bar must not be served from a stale closure.
  const inviteRef = useRef<string | null>(null);
  inviteRef.current = params.get("invite");
  const slugRef = useRef<string | null>(null);
  slugRef.current = slug;

  // The fingerprint of the context this adapter is running in — the same one
  // the funnel's answers are stored under, so the token and the answers can
  // never disagree about whose application this is. Read once, at mount, from
  // the synchronous halves of the URL.
  //
  // 🔴 EVERY member the funnel's scope carries has to be here too, and `blt` is
  // the live example of why. Leave it out and the two slots part company in one
  // direction only: an applicant who ran WITHOUT a continuation token leaves a
  // draft token behind under [slug, invite, null]; the next applicant arrives
  // with `?blt=`, their answers correctly fail to match and are cleared — and
  // this adapter, still fingerprinting without the token, MATCHES and appends
  // the new person's answers to the previous person's draft row. The clearing
  // that looks like it worked is what hides it.
  //
  // Mount-captured rather than read per render, matching the pages: the scope
  // is the identity of the run, not a value consumed at call time. `inviteRef`
  // above is per-render because it decides which token ADDRESSES the draft on
  // every single call; this decides whose run it is, once.
  const scopeRef = useRef<string | null>(null);
  if (scopeRef.current === null) {
    scopeRef.current = fingerprintScope({
      partnerSlug: slug,
      inviteToken: params.get("invite"),
      buyLandingToken: params.get("blt"),
    });
  }

  const stateRef = useRef<AdapterState | null>(null);
  if (stateRef.current === null) {
    stateRef.current = {
      // An explicit `?draft=` is the visitor following their own resume link:
      // addressed by hand, and the URL it came from carries its own context.
      draftToken: params.get("draft") ?? readStoredToken(role, scopeRef.current),
      rowVersion: null,
      exists: false,
      identityAtSave: null,
    };
  }

  return useMemo<PrequalDraftAdapter>(() => {
    const state = stateRef.current!;

    /** Exactly one token, invite first. Null when we can address nothing. */
    function addressable(): PrequalDraftToken | null {
      if (inviteRef.current) return { invite: inviteRef.current };
      if (state.draftToken) return { draft: state.draftToken };
      return null;
    }

    async function load(): Promise<PrequalDraftLoad | null> {
      const token = addressable();
      // No credential, no request. An organic first-time visitor should cost
      // the server nothing on landing.
      if (token === null) return null;

      try {
        const dto = await prequalDraftsApi.resume(slugRef.current, token);
        const answers = parseAnswers(dto.answersJson);
        const identity = {
          firstName: dto.firstName,
          lastName: dto.lastName,
          email: dto.email,
        };
        state.rowVersion = dto.rowVersion;
        state.exists = true;
        // Organic only — see `identityAtSave`. On the invited path the invite
        // is authoritative about who this is for, deliberately.
        state.identityAtSave = token.invite ? null : identity;
        if (answers === null) return null;
        return {
          role: dto.role,
          stepIndex: dto.stepIndex,
          stepCount: dto.stepCount,
          answers,
          identity,
          partnerSlug: dto.partnerSlug,
        };
      } catch {
        // A 404 here is the ORDINARY case, not an error: an invited visitor on
        // their first landing has a live invite and no draft behind it yet.
        // Anything else is transient, and the funnel is designed to keep
        // working without the server — sessionStorage still holds the answers.
        return null;
      }
    }

    async function persistStep(snapshot: PrequalDraftSnapshot): Promise<PrequalDraftWriteResult> {
      const invite = inviteRef.current;
      const answersJson = JSON.stringify(snapshot.answers);

      try {
        if (!state.exists) {
          // Nothing to create against yet on the organic path. Not an error —
          // the visitor simply has not told us who they are.
          if (!invite && !isIdentityComplete(snapshot.identity)) return { status: "skipped" };

          const created = await prequalDraftsApi.create(role, slugRef.current, invite, {
            firstName: snapshot.identity.firstName.trim(),
            lastName: snapshot.identity.lastName.trim(),
            email: snapshot.identity.email.trim(),
            answersJson,
            stepIndex: snapshot.stepIndex,
            // Stored on the ROW, so a resume on another device still credits
            // the campaign — session storage does not travel.
            attribution: loadAttribution() ?? undefined,
          });

          state.exists = true;
          state.rowVersion = created.rowVersion;
          // ORGANIC ONLY: the visitor's own typed identity is what the row now
          // holds, so a later change to it is a correction worth honouring. On
          // the invited path the server copied identity from the INVITE and
          // ignored this body — that is the design, not an accident, so nothing
          // is recorded and the invite stays authoritative.
          state.identityAtSave = invite ? null : snapshot.identity;

          // Organic only. The invited path returns no URL in any field, by
          // design, and must not be handed a second way back in.
          const minted = invite ? null : tokenFromResumeUrl(created.resumeUrl);
          if (minted !== null) {
            state.draftToken = minted;
            writeStoredToken(role, scopeRef.current!, minted);
          }
          return { status: "saved", resumeUrl: invite ? null : (created.resumeUrl ?? null) };
        }

        const token = addressable();
        if (token === null) return { status: "skipped" };

        const persisted = await prequalDraftsApi.step(slugRef.current, token, {
          rowVersion: state.rowVersion,
          stepIndex: snapshot.stepIndex,
          answersJson,
        });
        state.rowVersion = persisted.rowVersion;
        return { status: "saved" };
      } catch (error) {
        if (error instanceof PortalApiError && error.status === 409) {
          return { status: "conflict", message: CONFLICT_MESSAGE };
        }
        return { status: "failed", message: friendly(error) };
      }
    }

    async function finalize(request: PrequalFinalizeRequest): Promise<PrequalFinalizeResult> {
      // A late correction to who this is for wins over the saved copy — the
      // funnel asks for contact details on its LAST step, so the moment a
      // visitor notices their own email typo is usually after the draft exists,
      // and mailing their documents to the address they just fixed is not an
      // option.
      //
      // The correction now travels ON the draft finalize rather than forcing a
      // fall back to the ordinary submit path. That fallback honoured the fix
      // but left the draft behind: never consumed by a completion, and under the
      // never-purge retention default, never purged either — so a stranger's
      // contact details and partial financial answers would sit there
      // indefinitely. The server takes these fields for an ORGANIC draft only;
      // on an invited one the invite stays authoritative and they are ignored,
      // so a recipient cannot redirect a broker's outreach onto someone else.
      const corrected = identityMoved(state.identityAtSave, {
        firstName: request.firstName,
        lastName: request.lastName,
        email: request.email,
      });
      const token = state.exists ? addressable() : null;

      if (token !== null) {
        const answers = request.role === "Seller" ? request.seller : request.buyer;
        try {
          return await prequalDraftsApi.finalize(slugRef.current, token, {
            rowVersion: state.rowVersion,
            consentAccepted: request.consentAccepted,
            answersJson: JSON.stringify(answers ?? {}),
            phone: request.phone,
            website: request.website,
            // The server prefers the DRAFT's stored value; this covers a draft
            // created before the column existed.
            attribution: request.attribution,
            // The /buy continuation token. This path is REACHED by an ordinary
            // /buy visitor — saving progress, or Back-then-Continue, routes the
            // submit through here — so dropping it here would recreate the
            // duplicate lead the token exists to prevent.
            buyLandingToken: request.buyLandingToken,
            // The per-advertisement link token, for the same reason: without
            // it a Back-then-Continue visitor lands on the right team with no
            // advertisement credit while the ordinary path stamps one.
            advertisementToken: request.advertisementToken,
            // Sent only when they actually differ, so an unchanged submission is
            // byte-identical to what it was before.
            ...(corrected
              ? {
                  firstName: request.firstName,
                  lastName: request.lastName,
                  email: request.email,
                }
              : {}),
          });
        } catch (error) {
          // A 404 means the token stopped resolving — the invite was revoked or
          // the draft was purged between the last save and this click. Losing a
          // finished questionnaire to protect a reporting nicety is the wrong
          // trade, so the submission still goes through the ordinary path. Any
          // other failure (409, a validation 400) is the visitor's to see.
          if (!(error instanceof PortalApiError) || error.status !== 404) throw error;
        }
      }

      return prequalificationApi.finalize(request);
    }

    return { mode: "anonymous", role, load, persistStep, finalize };
  }, [role]);
}
